import React, {
  forwardRef,
  useRef,
  useMemo,
  useCallback,
  useState
} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";
import "./PhoneInput.scss";

const PhoneInput = forwardRef(
  (
    {
      initialValue = "",
      value,
      onChangeNumber,
      onChangeValidity,
      onCountryChange,
      handleChangeError,
      options = {},
      className,
      error = false,
      darkMode = false,
      isRequired = false,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [showErrorCode, setShowErrorCode] = useState(null);
    const [focused, setFocused] = useState(false);
    const account = useSelector((state) => state.account);
    const config = useSelector((state) => state.config);
    const preferedCountries = useMemo(() => config?.hub_countries.map((data) => data?.value?.toLowerCase()) || [], [config]);
    const allCountries = config?.allCountries || preferedCountries;
    const prevNumberRef = useRef(null);
    const prevValidityRef = useRef(null);
    const prevErrorCodeRef = useRef(null);
    const initialCountry = account?.country_code?.toLowerCase() || (preferedCountries?.length === 1 ? preferedCountries[0] : "");

    const errorMap = useMemo(() => ({
      "0": t("invalid_number"),
      "2": t("too_short"),
      "3": t("too_long"),
      "4": t("invalid_number"),
      "-99": t("invalid_number"),
    }), [t]);

    const defaultOptions = useMemo(() => ({
      onlyCountries: allCountries,
      countryOrder: preferedCountries,
      separateDialCode: true,
      initialCountry: initialCountry,
      useFullscreenPopup: false,
      autoPlaceholder: "aggressive",
      customPlaceholder: function (selectedCountryPlaceholder, selectedCountryData) {
        if (!selectedCountryData.iso2) {
          return t("enter_phone_number");
        }
        return selectedCountryPlaceholder;
      }
    }), [preferedCountries, initialCountry, allCountries]);

    const initOptions = useMemo(() => ({ ...defaultOptions, ...options }), [defaultOptions, options]);

    const handleChangeNumber = useCallback(
      (number) => {
        const currentNumber = number ?? "";
        if (prevNumberRef.current === currentNumber) return;
        if (currentNumber === "") {
          prevErrorCodeRef.current = -1;
          if (isRequired) {
            setShowErrorCode(t("invalid_number"));
          } else {
            setShowErrorCode(null);
          }
        }
        prevNumberRef.current = currentNumber;
        onChangeNumber?.(currentNumber);
      },
      [onChangeNumber, t, isRequired]
    );
  
    const handleChangeValidity = useCallback(
      (isValid) => {
        const errorCode = isValid ?? null;
        const prev = prevValidityRef.current;
        if (prev === errorCode) return;
        if (isRequired && prevNumberRef.current === "") {
          setShowErrorCode(t("invalid_number"));
        }
        prevValidityRef.current = errorCode;
        onChangeValidity?.(errorCode);
      },
      [onChangeValidity, t, isRequired]
    );

    const handleCountryChange = useCallback(
      (countryData) => {
        onCountryChange?.(countryData);
      },
      [onCountryChange]
    );

    const handleChangeErrorCode = useCallback(
      (errorCode) => {
        const prev = prevErrorCodeRef.current;
        if (prev === errorCode) return;
        if (isRequired && prevNumberRef.current === "") {
          prevErrorCodeRef.current = -1;
          setShowErrorCode(t("invalid_number"));
          return;
        }
        else if (prevNumberRef.current === "") {
          prevErrorCodeRef.current = -1;
          setShowErrorCode(null);
          return;
        }
        setShowErrorCode(errorCode !== null ? errorMap[errorCode] : null);
        prevErrorCodeRef.current = errorCode;
        handleChangeError?.(errorMap[errorCode] || null);
      }, [handleChangeError, errorMap, t, isRequired]);

    const validateOnBlur = () => {
      setFocused(true);
      if (isRequired && prevNumberRef?.current === "") {
        setShowErrorCode(t("invalid_number"));
      }
    }

    return (
      <div
        onBlur={validateOnBlur}
        className={`phone-input-wrapper ${darkMode ? "dark_mode" : ""} ${focused && showErrorCode ? "error" : ""}`}
      >
        <IntlTelInput
          ref={ref}
          initialValue={initialValue || ""}
          onChangeErrorCode={handleChangeErrorCode}
          onChangeValidity={handleChangeValidity}
          onChangeNumber={handleChangeNumber}
          onCountryChange={handleCountryChange}
          initOptions={initOptions}
          {...rest}
        />
        {focused && showErrorCode && <div className="phone-input-error">{showErrorCode}</div>}
      </div>
    );
  }
);

export default PhoneInput;
